import LegalDoc, { Section, SubHead, List, GameNote } from '@/components/LegalDoc'
import type { CommunityData } from '@/useCommunity'
import { useLanguage } from '@/localization'

/**
 * /privacy — the platform-wide Privacy Policy covering uliunai.com and every
 * game subdomain (KF1, Minecraft, …). Game-specific data practices are scoped
 * with <GameNote> blocks so one document stays accurate as games are added.
 */
export default function PrivacyPolicy({ social }: { social: CommunityData['social'] }) {
  const { t } = useLanguage()

  return (
    <LegalDoc
      eyebrow="How we handle your data"
      title="Privacy"
      accent="Policy"
      lastUpdated="June 2026"
      social={social}
      intro={
        <>
          <p>
            {t(
              'Welcome to Uliunai.com ("we," "our," or "us"), a community-operated platform hosting game servers and community sites across multiple games. This Privacy Policy explains how we collect, use, store, and protect your information when you visit any Uliunai site, use our services, or connect to our game servers. It applies to uliunai.com and all of its game subdomains.',
            )}
          </p>
          <p>
            {t(
              'By using our websites and services you acknowledge that you have read and understood this policy. If you do not agree with our practices, please discontinue use of our services.',
            )}
          </p>
        </>
      }
    >
      <Section title="Data We Collect">
        <p>
          We collect only what is necessary to operate our services and maintain the community.
        </p>

        <SubHead>Contact Form Submissions</SubHead>
        <p>When you submit a message through a contact form, we collect what you voluntarily provide:</p>
        <List items={['Your name', 'Your email address', 'The subject of your message', 'The content of your message']} />

        <SubHead>Game Server Data</SubHead>
        <p>
          When you connect to one of our game servers, we automatically collect gameplay and
          connection data needed to run the server, enforce rules, and power our statistics and
          leaderboards. The exact data depends on the game:
        </p>

        <GameNote game="Killing Floor 1">
          <List
            items={[
              'Your in-game player name',
              'Your Steam ID (as provided by the game engine)',
              'Gameplay statistics such as kills, deaths, perk levels, and wave progress',
              'Connection metadata such as ping and session duration',
              'Your IP address (for server administration and anti-cheat purposes)',
            ]}
          />
          <p>
            This is collected through our custom server-side mutator (UliunaiStats), which exports
            live game state to display real-time and historical statistics on the KF1 site.
          </p>
        </GameNote>

        <GameNote game="Minecraft">
          <List
            items={[
              'Your Minecraft username and account UUID (as provided by Mojang/Microsoft authentication)',
              'Gameplay and connection data such as playtime, session activity, and chat needed for moderation',
              'Your IP address (for server administration and anti-grief/anti-cheat purposes)',
            ]}
          />
          <p>
            The Minecraft servers do not currently process any payment information.
          </p>
        </GameNote>

        <SubHead>Payment Data</SubHead>
        <p>
          Where a game offers optional paid features (such as VIP memberships or donations), payments
          are processed by <strong className="text-stone-900">Stripe</strong>. We never see or store
          your full card details &mdash; Stripe handles that directly. We retain only the limited
          metadata needed to grant and manage your benefits (for example, the associated game account
          ID, tier, and subscription status). VIP/donation payments currently apply to Killing
          Floor 1 only.
        </p>
      </Section>

      <Section title="How We Use Your Data">
        <p>The data we collect is used solely to:</p>
        <List
          items={[
            <><strong className="text-stone-900">Operate servers</strong> &mdash; maintain, monitor, and improve the performance and stability of our game servers.</>,
            <><strong className="text-stone-900">Respond to inquiries</strong> &mdash; reply to your contact submissions, questions, and support requests.</>,
            <><strong className="text-stone-900">Manage the community</strong> &mdash; enforce rules, resolve disputes, and prevent cheating or abuse.</>,
            <><strong className="text-stone-900">Display statistics</strong> &mdash; show player stats, rankings, and match history for the benefit of the community.</>,
            <><strong className="text-stone-900">Process payments</strong> &mdash; grant and manage optional paid benefits where offered.</>,
          ]}
        />
        <p>
          We do not sell, trade, or rent your personal information to third parties, and we do not
          use your data for advertising beyond communications directly related to our community
          services.
        </p>
      </Section>

      <Section title="Cookies">
        <p>
          Our websites use only essential cookies strictly necessary for the site to function. We do
          not use tracking, analytics, or advertising cookies, and we do not use third-party
          analytics services such as Google Analytics or Facebook Pixel.
        </p>
        <p>
          Essential cookies may include session identifiers required for authentication to server
          administration panels. These are temporary and deleted when you close your browser or log
          out.
        </p>
      </Section>

      <Section title="Third-Party Services">
        <p>Our sites link to or rely on the following third-party services, each governed by its own privacy policy:</p>
        <List
          items={[
            <><strong className="text-stone-900">Steam</strong> &mdash; profiles, store pages, and community resources. Operated by Valve Corporation.</>,
            <><strong className="text-stone-900">Discord</strong> &mdash; our community chat. Operated by Discord Inc.</>,
            <><strong className="text-stone-900">Stripe</strong> &mdash; payment processing for optional paid features. Operated by Stripe, Inc.</>,
            <><strong className="text-stone-900">Cloudflare</strong> &mdash; CDN and SSL/TLS for our sites. Operated by Cloudflare, Inc.</>,
            <><strong className="text-stone-900">Google Fonts</strong> &mdash; we load Orbitron and Rajdhani from Google&rsquo;s CDN, which may receive limited technical data such as your IP address.</>,
          ]}
        />
        <p>
          We are not responsible for the privacy practices of these services. We encourage you to
          review their respective policies.
        </p>
      </Section>

      <Section title="Data Retention">
        <List
          items={[
            <><strong className="text-stone-900">Contact form data</strong> &mdash; kept as long as needed to resolve your inquiry and for a reasonable follow-up period.</>,
            <><strong className="text-stone-900">Game statistics</strong> &mdash; retained as part of the community&rsquo;s historical record (leaderboards, rankings, match history).</>,
            <><strong className="text-stone-900">Server logs and IP addresses</strong> &mdash; retained for as long as the associated administrative action (such as a ban) is in effect.</>,
            <><strong className="text-stone-900">Payment metadata</strong> &mdash; retained as required to manage your benefits and to meet legal and accounting obligations.</>,
          ]}
        />
        <p>
          To request deletion of your personal data, contact us using the methods below. Note that we
          may be unable to delete aggregated or anonymized statistics already incorporated into
          community leaderboards.
        </p>
      </Section>

      <Section title="Data Security">
        <p>
          We take reasonable measures to protect your data from unauthorized access, alteration,
          disclosure, or destruction. Our sites are served over HTTPS with SSL/TLS provided by
          Cloudflare, and server access is restricted to authorized administrators.
        </p>
        <p>
          However, no method of transmission or storage is completely secure. While we strive to use
          commercially acceptable means to protect your information, we cannot guarantee its absolute
          security.
        </p>
      </Section>

      <Section title="Children's Privacy">
        <p>
          Our services are not directed at children under 13. Some of our games (such as Killing
          Floor) are mature-rated, and our platform is intended for players who meet the relevant age
          requirements. We do not knowingly collect personal information from children under 13. If
          we become aware that we have, we will delete it as soon as possible &mdash; please contact
          us if you believe this has occurred.
        </p>
      </Section>

      <Section title="Your Rights">
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <List
          items={[
            'Access the personal data we hold about you',
            'Request correction of inaccurate data',
            'Request deletion of your data, subject to our legitimate interests and legal obligations',
            'Object to or restrict the processing of your data',
            'Request data portability where applicable',
          ]}
        />
        <p>To exercise any of these rights, contact us using the methods below. We will respond within a reasonable timeframe.</p>
      </Section>

      <Section title="Changes to This Policy">
        <p>
          We may update this Privacy Policy to reflect changes in our practices, services, or
          applicable laws. When we do, we will update the &ldquo;Last updated&rdquo; date above. Your
          continued use of our sites and services after changes constitutes acceptance of the updated
          policy.
        </p>
      </Section>
    </LegalDoc>
  )
}
